import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';
import type { DeviceType } from '@/types/deviceType';
import { humanizeIdentifier } from '@/types/deviceType';
import type { Credential } from './useCredentials';

const backendApi = config.backendApi;

/** A third-party network controller integration (UniFi / Omada / …). */
export interface Controller {
  id: string;
  organizationId: string;
  divisionId: string | null;
  regionId: string;
  name: string;
  type: string;
  baseUrl: string;
  site: string | null;
  credentialId: string;
  verifyTls: boolean;
  enabled: boolean;
  syncIntervalSecs: number | null;
  syncHealth: string;
  deviceCount: number;
  clientCount: number;
  /** Sites a site directory last listed. Absent for device controllers. */
  siteCount?: number | null;
  /** Set on a site controller adopted from (or linked to) a discovered site. */
  discoveredSiteId?: string | null;
  /** A site controller's Site Manager parent. Only on the single-controller read. */
  managedThrough?: ManagedThrough | null;
  /** A Site Manager directory placed in an MSP customer's region: that customer's name. */
  inCustomerRegion?: string | null;
  /** A Site Manager directory's sites still waiting on a decision. Each site counts once. */
  sitesToReview?: SitesToReview | null;
  lastSyncAt: string | null;
  lastError: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ControllerListResponse {
  controllers: Controller[];
}

export interface CreateControllerRequest {
  name: string;
  type: string;
  baseUrl: string;
  site?: string | null;
  credentialId: string;
  verifyTls?: boolean;
  enabled?: boolean;
  description?: string | null;
  divisionId?: string | null;
  regionId: string;
}

export interface UpdateControllerRequest {
  name?: string;
  baseUrl?: string;
  site?: string | null;
  credentialId?: string;
  verifyTls?: boolean;
  enabled?: boolean;
  description?: string | null;
}

const fetchControllers = async (): Promise<Controller[]> => {
  const response = await axios.get<ControllerListResponse>(`${backendApi}/api/controllers/`, {
    withCredentials: true,
  });
  // Defensive: never let a non-array response reach a `.filter`/`.map` caller.
  return Array.isArray(response.data.controllers) ? response.data.controllers : [];
};

export function useControllers(
  organizationId: string | null | undefined,
): UseQueryResult<Controller[]> {
  const contextReady = useActiveContextReady();
  return useQuery<Controller[]>({
    queryKey: ['controllers', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required to fetch controllers.');
      return fetchControllers();
    },
    enabled: !!organizationId && contextReady,
  });
}

export function useController(controllerId: string | undefined): UseQueryResult<Controller> {
  const contextReady = useActiveContextReady();
  return useQuery<Controller>({
    queryKey: ['controller', controllerId],
    queryFn: async () => {
      if (!controllerId) throw new Error('controllerId is required');
      const response = await axios.get<Controller>(
        `${backendApi}/api/controllers/${controllerId}`,
        { withCredentials: true },
      );
      return response.data;
    },
    enabled: !!controllerId && contextReady,
    // Sync health is refreshed by the gateway every ~60s; poll so the UI
    // reflects "ok / N devices" without a manual reload.
    refetchInterval: 30000,
  });
}

/** A device imported from a controller (for the controller detail page). */
export interface ControllerDevice {
  id: string;
  name: string | null;
  ipAddress: string | null;
  deviceType: DeviceType;
  status: string;
  macAddress: string | null;
  model: string | null;
}

interface ControllerDeviceListResponse {
  devices: ControllerDevice[];
}

export function useControllerDevices(
  controllerId: string | undefined,
): UseQueryResult<ControllerDevice[]> {
  const contextReady = useActiveContextReady();
  return useQuery<ControllerDevice[]>({
    queryKey: ['controller-devices', controllerId],
    queryFn: async () => {
      if (!controllerId) throw new Error('controllerId is required');
      const response = await axios.get<ControllerDeviceListResponse>(
        `${backendApi}/api/controllers/${controllerId}/devices`,
        { withCredentials: true },
      );
      return Array.isArray(response.data.devices) ? response.data.devices : [];
    },
    enabled: !!controllerId && contextReady,
    refetchInterval: 30000,
  });
}

/**
 * A directory's to-do list — decisions nobody has made yet, not faults (an
 * offline console is an incident). Each site counts once; a site gone from the
 * account is "gone" whatever its state, and dismissed sites never count.
 */
export interface SitesToReview {
  notPlaced: number;
  readyToAdopt: number;
  gone: number;
}

/** The Sites table filter each to-review count opens, as a `?sites=` value. */
export const SITES_TO_REVIEW_PARTS: {
  key: keyof SitesToReview;
  filter: 'new' | 'matched' | 'missing';
  label: string;
}[] = [
  { key: 'notPlaced', filter: 'new', label: 'not placed' },
  { key: 'readyToAdopt', filter: 'matched', label: 'ready to adopt' },
  { key: 'gone', filter: 'missing', label: 'gone from account' },
];

/** The Site Manager parent a site controller is managed through. */
export interface ManagedThrough {
  parentId: string;
  parentName: string;
  discoveredSiteId: string;
  consoleName: string | null;
  consoleState: string | null;
  missingSince: string | null;
}

/** A console site a site directory (UniFi Site Manager) discovered. */
export interface DiscoveredSite {
  id: string;
  externalConsoleId: string;
  externalSiteId: string;
  /** The name the console's local API addresses the site by (`default`). */
  siteReference: string;
  siteName: string | null;
  consoleName: string | null;
  consoleModel: string | null;
  consoleFirmware: string | null;
  /** The address the console reports for itself (its LAN address). */
  consoleAddress: string | null;
  consolePublicAddress: string | null;
  consoleOwner: boolean | null;
  consoleState: string | null;
  consoleStateChangedAt: string | null;
  regionId: string | null;
  state: 'new' | 'matched' | 'adopted' | 'dismissed';
  firstSeenAt: string;
  lastSeenAt: string;
  missingSince: string | null;
  /** The site controller managing it, once adopted. */
  siteController: { id: string; name: string; syncHealth: string } | null;
}

interface DiscoveredSiteListResponse {
  sites: DiscoveredSite[];
}

export function useControllerSites(
  controllerId: string | undefined,
  enabled: boolean,
): UseQueryResult<DiscoveredSite[]> {
  const contextReady = useActiveContextReady();
  return useQuery<DiscoveredSite[]>({
    queryKey: ['controller-sites', controllerId],
    queryFn: async () => {
      if (!controllerId) throw new Error('controllerId is required');
      const response = await axios.get<DiscoveredSiteListResponse>(
        `${backendApi}/api/controllers/${controllerId}/sites`,
        { withCredentials: true },
      );
      return Array.isArray(response.data.sites) ? response.data.sites : [];
    },
    enabled: !!controllerId && enabled && contextReady,
    refetchInterval: 30000,
  });
}

/** Invalidate everything a workflow action on a discovered site can change. */
function useInvalidateSites(parentId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['controller-sites', parentId] });
    void queryClient.invalidateQueries({ queryKey: ['controllers'] });
    void queryClient.invalidateQueries({ queryKey: ['controller'] });
  };
}

async function postSiteAction<T>(parentId: string, siteId: string, action: string, body: unknown): Promise<T> {
  const response = await axios.post<T>(
    `${backendApi}/api/controllers/${parentId}/sites/${siteId}/${action}`,
    body,
    { withCredentials: true },
  );
  return response.data;
}

/** Set (or, with null, clear) the customer region of a discovered site. */
export function useMatchSite(parentId: string) {
  const invalidate = useInvalidateSites(parentId);
  return useMutation<DiscoveredSite, Error, { siteId: string; regionId: string | null }>({
    mutationFn: ({ siteId, regionId }) => postSiteAction(parentId, siteId, 'match', { regionId }),
    onSuccess: invalidate,
  });
}

export function useDismissSite(parentId: string) {
  const invalidate = useInvalidateSites(parentId);
  return useMutation<DiscoveredSite, Error, string>({
    mutationFn: (siteId) => postSiteAction(parentId, siteId, 'dismiss', {}),
    onSuccess: invalidate,
  });
}

export function useRestoreSite(parentId: string) {
  const invalidate = useInvalidateSites(parentId);
  return useMutation<DiscoveredSite, Error, string>({
    mutationFn: (siteId) => postSiteAction(parentId, siteId, 'restore', {}),
    onSuccess: invalidate,
  });
}

/**
 * Separate an adopted site from its site controller. The controller keeps
 * running on its own; the site returns to "matched" and can be adopted again.
 */
export function useUnlinkSite(parentId: string) {
  const invalidate = useInvalidateSites(parentId);
  return useMutation<DiscoveredSite, Error, string>({
    mutationFn: (siteId) => postSiteAction(parentId, siteId, 'unlink', {}),
    onSuccess: invalidate,
  });
}

/** Adopt by linking a controller the MSP already made, or by creating one. */
export type AdoptSiteRequest =
  | { controllerId: string }
  | {
      regionId: string;
      name: string;
      baseUrl: string;
      credentialId: string;
      verifyTls: boolean;
    };

export interface AdoptSiteResponse {
  site: DiscoveredSite;
  controller: Controller;
  created: boolean;
}

export function useAdoptSite(parentId: string) {
  const invalidate = useInvalidateSites(parentId);
  return useMutation<AdoptSiteResponse, Error, { siteId: string; body: AdoptSiteRequest }>({
    mutationFn: ({ siteId, body }) => postSiteAction(parentId, siteId, 'adopt', body),
    onSuccess: invalidate,
  });
}

/** How a site controller reaches its console. */
export type ConsoleConnection = 'cloud' | 'lan';

/** The Site Manager relay URL for a console: the site controller's base URL when polled through UniFi's cloud. */
export function cloudConsoleUrl(consoleId: string): string {
  return `https://api.ui.com/v1/connector/consoles/${consoleId}`;
}

export function connectionOf(baseUrl: string): ConsoleConnection {
  return baseUrl.includes('api.ui.com/v1/connector/consoles/') ? 'cloud' : 'lan';
}

/** Where to create a Site Manager key scoped to one console (unifi.ui.com). */
export const SITE_MANAGER_API_KEYS_URL = 'https://unifi.ui.com/settings/api-keys';

/** The console's own Integrations page, opened remotely through unifi.ui.com. */
export function consoleIntegrationsUrl(consoleId: string, siteReference: string): string {
  return `https://unifi.ui.com/consoles/${consoleId}/network/${encodeURIComponent(siteReference)}/integrations`;
}

export function useCreateController(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<Controller, Error, CreateControllerRequest>({
    mutationFn: async (data) => {
      const response = await axios.post<Controller>(`${backendApi}/api/controllers/`, data, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['controllers', organizationId] });
    },
  });
}

export function useUpdateController(controllerId: string, organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<Controller, Error, UpdateControllerRequest>({
    mutationFn: async (data) => {
      const response = await axios.put<Controller>(
        `${backendApi}/api/controllers/${controllerId}`,
        data,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['controllers', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['controller', controllerId] });
    },
  });
}

export function useDeleteController(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation<string, Error, string>({
    mutationFn: async (controllerId) => {
      await axios.delete(`${backendApi}/api/controllers/${controllerId}`, {
        withCredentials: true,
      });
      return controllerId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['controllers', organizationId] });
    },
  });
}

/** What the add/edit form and detail page need to know about a controller family. */
export interface ControllerTypeInfo {
  value: string;
  label: string;
  /** Whether the gateway has a backend for it yet. */
  implemented: boolean;
  /**
   * A vendor account's directory of consoles and sites (UniFi Site Manager)
   * rather than a controller: it discovers sites and imports no devices.
   */
  directory?: boolean;
  /** Cloud APIs live at one address, so the form fills it in. */
  fixedBaseUrl?: string;
  /** The credential kinds it can sign in with. */
  credentialTypes: Credential['type'][];
}

const LOGIN_OR_KEY: Credential['type'][] = ['usernamePassword', 'apiKey'];

/** Controller families. */
export const CONTROLLER_TYPES: ControllerTypeInfo[] = [
  { value: 'unifi', label: 'Ubiquiti UniFi', implemented: true, credentialTypes: LOGIN_OR_KEY },
  {
    value: 'unifi_site_manager',
    label: 'UniFi Site Manager',
    implemented: true,
    directory: true,
    fixedBaseUrl: 'https://api.ui.com',
    credentialTypes: ['apiKey'],
  },
  { value: 'omada', label: 'TP-Link Omada', implemented: false, credentialTypes: LOGIN_OR_KEY },
  { value: 'netgear_insight', label: 'Netgear Insight', implemented: false, credentialTypes: LOGIN_OR_KEY },
  { value: 'meraki', label: 'Cisco Meraki', implemented: false, credentialTypes: LOGIN_OR_KEY },
  { value: 'mist', label: 'Juniper Mist', implemented: false, credentialTypes: LOGIN_OR_KEY },
  { value: 'aruba_central', label: 'Aruba Central', implemented: false, credentialTypes: LOGIN_OR_KEY },
];

export function controllerTypeInfo(type: string): ControllerTypeInfo | undefined {
  return CONTROLLER_TYPES.find((t) => t.value === type);
}

export function controllerTypeLabel(type: string): string {
  return controllerTypeInfo(type)?.label ?? humanizeIdentifier(type);
}

export function isDirectoryType(type: string): boolean {
  return controllerTypeInfo(type)?.directory === true;
}

/** A discovered console's connection state, as Site Manager reports it. */
export function consoleStateLabel(state: string | null): string {
  switch (state) {
    case 'connected':
      return 'Online';
    case 'disconnected':
      return 'Offline';
    case null:
    case '':
      return 'Unknown';
    default:
      return humanizeIdentifier(state);
  }
}

export const CONSOLE_STATE_STYLE: Record<string, string> = {
  connected: 'bg-sev-4/15 text-sev-4',
  disconnected: 'bg-sev-1/15 text-sev-1',
};

/** Where a discovered site is in the MSP's workflow. */
export function siteStateLabel(state: DiscoveredSite['state']): string {
  switch (state) {
    case 'new':
      return 'Not placed';
    case 'matched':
      return 'Ready to adopt';
    case 'adopted':
      return 'Adopted';
    case 'dismissed':
      return 'Dismissed';
  }
}

/** Tailwind classes for a sync-health badge (mirrors device status colors). */
export const SYNC_HEALTH_STYLE: Record<string, string> = {
  ok: 'bg-sev-4/15 text-sev-4',
  auth_failed: 'bg-sev-1/15 text-sev-1',
  unreachable: 'bg-sev-1/15 text-sev-1',
  error: 'bg-sev-1/15 text-sev-1',
  unknown: 'bg-muted text-muted-foreground',
};

export function syncHealthLabel(health: string): string {
  switch (health) {
    case 'ok':
      return 'Healthy';
    case 'auth_failed':
      return 'Auth failed';
    case 'unreachable':
      return 'Unreachable';
    case 'error':
      return 'Error';
    default:
      return 'Pending';
  }
}
